import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Download,
  ExternalLink,
  Loader2,
  Package,
  ShoppingCart,
  Truck,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { farmGet } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const PERIODS = [
  { id: "7", label: "Ultimi 7 giorni", days: 7 },
  { id: "30", label: "Ultimi 30 giorni", days: 30 },
  { id: "90", label: "Ultimi 90 giorni", days: 90 },
];

const PIE_COLORS = ["#0F5C4C", "#7C3AED"];

function daysAgoISO(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function parseDay(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return s;
}

function inRange(iso, from, to) {
  const d = parseDay(iso);
  if (!d) return false;
  return d >= from && d <= to;
}

function pctChange(curr, prev) {
  if (prev == null || prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

function formatEuro(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `€ ${Number(n).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function ordineTotale(o) {
  let tot = 0;
  let has = false;
  for (const r of o.righe || []) {
    if (r.prezzo != null && !Number.isNaN(Number(r.prezzo))) {
      has = true;
      const q = Number(r.quantita_consegnata ?? r.quantita_confermata ?? r.quantita_richiesta ?? r.quantita ?? 1);
      tot += Number(r.prezzo) * q;
    }
  }
  return has ? tot : null;
}

function ordineOrgId(o) {
  return o.organization_id || o.organizationId || (o.organization_ids || [])[0] || null;
}

function TrendHint({ value }) {
  if (value == null) return <p className="mt-0.5 text-[10px] text-slate-400">vs periodo prec.</p>;
  const up = value >= 0;
  return (
    <p className={`mt-0.5 text-[10px] font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}>
      {up ? "+" : ""}
      {value}% vs periodo prec.
    </p>
  );
}

function KpiTile({ icon: Icon, label, value, trend, hint, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
          {hint ? <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p> : <TrendHint value={trend} />}
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.emerald}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function performanceBadge(row) {
  const rate = row.consegneTotal ? row.consegneOk / row.consegneTotal : null;
  const ordRate = row.ordiniTotal ? row.ordiniEvasi / row.ordiniTotal : null;
  const score = [rate, ordRate].filter((x) => x != null);
  if (!score.length) {
    return { label: "N/D", className: "border-slate-200 bg-slate-50 text-slate-500" };
  }
  const avg = score.reduce((a, b) => a + b, 0) / score.length;
  if (avg >= 0.9) return { label: "Eccellente", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  if (avg >= 0.75) return { label: "Ottima", className: "border-sky-200 bg-sky-50 text-sky-700" };
  if (avg >= 0.5) return { label: "Buona", className: "border-amber-200 bg-amber-50 text-amber-800" };
  return { label: "Da migliorare", className: "border-rose-200 bg-rose-50 text-rose-700" };
}

function ReportDetailPanel({ stats, periodLabel, onExportCsv, onClose }) {
  return (
    <aside className="w-full shrink-0 xl:w-[320px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Report selezionato
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              Andamento farmacia
            </p>
          </div>
          <button type="button" className="text-xs text-slate-400 hover:text-slate-600 xl:hidden" onClick={onClose}>
            Chiudi
          </button>
        </div>
        <div className="space-y-4 overflow-y-auto p-4 text-xs">
          <dl className="space-y-1.5">
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Periodo</dt>
              <dd className="font-medium text-slate-800">{periodLabel}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Strutture incluse</dt>
              <dd className="font-medium text-slate-800">{stats.struttureCount}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Pratiche completate</dt>
              <dd className="font-medium text-slate-800">{stats.praticheDone}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Ordini evasi</dt>
              <dd className="font-medium text-slate-800">{stats.ordiniEvasi}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Consegne ok</dt>
              <dd className="font-medium text-slate-800">
                {stats.consegneOk}
                {stats.consegneTotal ? ` / ${stats.consegneTotal}` : ""}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Assistiti</dt>
              <dd className="font-medium text-slate-800">{stats.assistiti}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Valore extra</dt>
              <dd className="font-medium text-slate-800">
                {stats.valoreExtra != null ? formatEuro(stats.valoreExtra) : `${stats.extraUnita} u.`}
              </dd>
            </div>
          </dl>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Stato report
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Dati aggiornati · {formatWhen(stats.updatedAt)}
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Fonti: KPI, ordini, consegne, panoramiche
              </li>
              <li className="flex items-start gap-2 text-slate-500">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                Limite elenco ordini/consegne (ultime 100/200). Trend solo se periodo prec. ha dati.
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Azioni rapide
            </p>
            <div className="grid gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={onExportCsv}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Esporta CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => toast.message("Export PDF non disponibile: usa CSV.")}
              >
                Scarica PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => toast.message("Invio riepilogo non configurato: esporta CSV e invialo manualmente.")}
              >
                Invia riepilogo
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-auto border-t border-slate-100 p-3">
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to="/farmacia/amministrazione">
              Apri amministrazione
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

export default function FarmaciaReport() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [periodId, setPeriodId] = useState("30");
  const [orgFilter, setOrgFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [strutture, setStrutture] = useState([]);
  const [kpi, setKpi] = useState({});
  const [pratiche, setPratiche] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [consegne, setConsegne] = useState([]);
  const [clientiCount, setClientiCount] = useState(0);
  const [ospitiCount, setOspitiCount] = useState(0);
  const [extraRows, setExtraRows] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const period = PERIODS.find((p) => p.id === periodId) || PERIODS[1];

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [strRes, kpiRes, cliRes, ospRes, ordRes, consRes] = await Promise.all([
        farmGet("/farmacia/strutture", token, { lite: true }).catch(() => ({ items: [] })),
        farmGet("/farmacia/ricette/kpi", token).catch(() => ({})),
        farmGet("/farmacia/clienti", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/ospiti", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/ordini", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/consegne", token).catch(() => ({ items: [] })),
      ]);
      const struttureItems = strRes.items || [];
      setStrutture(struttureItems);
      setKpi(kpiRes || {});
      setClientiCount((cliRes.items || []).length);
      setOspitiCount(
        typeof kpiRes?.ospiti_aderenti === "number"
          ? kpiRes.ospiti_aderenti
          : (ospRes.items || []).length
      );
      setOrdini(ordRes.items || []);
      setConsegne(consRes.items || []);

      const fromStrutture = [];
      await Promise.all(
        struttureItems.slice(0, 12).map(async (s) => {
          try {
            const pan = await farmGet("/farmacia/ricette/panoramica", token, {
              organization_id: s.organization_id,
            });
            const nomeStr = s.denominazione || s.nome || s.organization_id;
            for (const o of pan.tutti_ospiti || []) {
              fromStrutture.push({
                organization_id: s.organization_id,
                struttura_nome: o.struttura_nome || nomeStr,
                stato: o.stato || "nessuna_pratica",
                ultima_attivita: o.ultima_attivita,
              });
            }
          } catch {
            /* ignore */
          }
        })
      );
      setPratiche(fromStrutture);

      const periodo = new Date().toISOString().slice(0, 7);
      const extras = await Promise.all(
        struttureItems.map((s) =>
          farmGet("/farmacia/fatture/riepilogo-extra", token, {
            organization_id: s.organization_id,
            periodo,
          })
            .then((r) =>
              (r.items || []).map((e) => ({
                ...e,
                organization_id: s.organization_id,
              }))
            )
            .catch(() => [])
        )
      );
      setExtraRows(extras.flat());
      setUpdatedAt(new Date().toISOString());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento report");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const ranges = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const currFrom = daysAgoISO(period.days - 1);
    const prevTo = daysAgoISO(period.days);
    const prevFrom = daysAgoISO(period.days * 2 - 1);
    return { today, currFrom, prevFrom, prevTo };
  }, [period.days]);

  const filteredOrg = useCallback(
    (orgId) => orgFilter === "all" || orgId === orgFilter,
    [orgFilter]
  );

  const stats = useMemo(() => {
    const { currFrom, today, prevFrom, prevTo } = ranges;

    const praticheCurr = pratiche.filter(
      (p) =>
        filteredOrg(p.organization_id) &&
        p.stato === "completata" &&
        inRange(p.ultima_attivita, currFrom, today)
    );
    const pratichePrev = pratiche.filter(
      (p) =>
        filteredOrg(p.organization_id) &&
        p.stato === "completata" &&
        inRange(p.ultima_attivita, prevFrom, prevTo)
    );
    // Se manca data attività, conta comunque le completate nel periodo corrente come stock
    const praticheDoneStock = pratiche.filter(
      (p) => filteredOrg(p.organization_id) && p.stato === "completata"
    ).length;
    const praticheDone =
      praticheCurr.length ||
      (pratichePrev.length === 0 ? praticheDoneStock : praticheCurr.length);

    const ordiniCurr = ordini.filter((o) => {
      const oid = ordineOrgId(o);
      if (!filteredOrg(oid)) return false;
      return inRange(o.updated_at || o.created_at, currFrom, today);
    });
    const ordiniPrev = ordini.filter((o) => {
      const oid = ordineOrgId(o);
      if (!filteredOrg(oid)) return false;
      return inRange(o.updated_at || o.created_at, prevFrom, prevTo);
    });
    const isEvaso = (o) => ["consegnato", "completato"].includes(o.stato);
    const ordiniEvasi = ordiniCurr.filter(isEvaso).length;
    const ordiniEvasiPrev = ordiniPrev.filter(isEvaso).length;

    const consCurr = consegne.filter(
      (c) => filteredOrg(c.organization_id) && inRange(c.data || c.updated_at, currFrom, today)
    );
    const consPrev = consegne.filter(
      (c) => filteredOrg(c.organization_id) && inRange(c.data || c.updated_at, prevFrom, prevTo)
    );
    const consOk = consCurr.filter((c) => ["consegnata", "parziale"].includes(c.stato)).length;
    const consFail = consCurr.filter((c) => ["problema", "riprogrammata"].includes(c.stato)).length;
    const consOkPrev = consPrev.filter((c) => ["consegnata", "parziale"].includes(c.stato)).length;
    const puntualiPct = consCurr.length ? Math.round((consOk / consCurr.length) * 100) : null;
    const puntualiPctPrev = consPrev.length ? Math.round((consOkPrev / consPrev.length) * 100) : null;

    let valoreExtra = 0;
    let hasEuro = false;
    let extraUnita = 0;
    for (const o of ordiniCurr.filter(isEvaso)) {
      const t = ordineTotale(o);
      if (t != null) {
        hasEuro = true;
        valoreExtra += t;
      }
      for (const r of o.righe || []) {
        extraUnita += Number(r.quantita_consegnata || 0);
      }
    }
    if (!extraUnita) {
      for (const e of extraRows) {
        if (!filteredOrg(e.organization_id)) continue;
        extraUnita += e.totale_unita || 0;
      }
    }

    const assistiti =
      (typeof kpi.ospiti_aderenti === "number" ? kpi.ospiti_aderenti : ospitiCount) +
      (typeof kpi.clienti_diretti === "number" ? kpi.clienti_diretti : clientiCount);

    // Weekly pratiche (last ~5 weeks in current window)
    const weekBuckets = [];
    const start = new Date(`${currFrom}T12:00:00`);
    const end = new Date(`${today}T12:00:00`);
    const cursor = new Date(start);
    while (cursor <= end) {
      const wFrom = cursor.toISOString().slice(0, 10);
      const wEnd = new Date(cursor);
      wEnd.setDate(wEnd.getDate() + 6);
      if (wEnd > end) wEnd.setTime(end.getTime());
      const wTo = wEnd.toISOString().slice(0, 10);
      const label = cursor.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
      const count = pratiche.filter(
        (p) =>
          filteredOrg(p.organization_id) &&
          p.stato === "completata" &&
          inRange(p.ultima_attivita, wFrom, wTo)
      ).length;
      weekBuckets.push({ label, count });
      cursor.setDate(cursor.getDate() + 7);
    }
    if (weekBuckets.every((w) => w.count === 0) && praticheDoneStock > 0) {
      // fallback: single point with stock count so chart isn't empty
      weekBuckets.length = 0;
      weekBuckets.push({ label: "Attuali", count: praticheDoneStock });
    }

    const ordiniByStruttura = {};
    for (const s of strutture) {
      if (!filteredOrg(s.organization_id)) continue;
      ordiniByStruttura[s.organization_id] = {
        name: (s.denominazione || s.nome || "").slice(0, 18),
        count: 0,
        orgId: s.organization_id,
      };
    }
    for (const o of ordiniCurr) {
      const oid = ordineOrgId(o);
      if (!oid || !ordiniByStruttura[oid]) continue;
      ordiniByStruttura[oid].count += 1;
    }
    const barOrdini = Object.values(ordiniByStruttura)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const ospitiN =
      typeof kpi.ospiti_aderenti === "number" ? kpi.ospiti_aderenti : ospitiCount;
    const clientiN =
      typeof kpi.clienti_diretti === "number" ? kpi.clienti_diretti : clientiCount;
    const pieAssistiti = [
      { name: "Strutture", value: ospitiN, color: PIE_COLORS[0] },
      { name: "Clienti diretti", value: clientiN, color: PIE_COLORS[1] },
    ].filter((x) => x.value > 0);

    const byProduct = {};
    for (const e of extraRows) {
      if (!filteredOrg(e.organization_id)) continue;
      for (const r of e.righe || []) {
        const name = r.prodotto_nome || r.prodotto_codice || "Altro";
        byProduct[name] = (byProduct[name] || 0) + (r.quantita_consegnata || 0);
      }
    }
    const extraTop = Object.entries(byProduct)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);

    const tableRows = strutture
      .filter((s) => filteredOrg(s.organization_id))
      .map((s) => {
        const oid = s.organization_id;
        const ospiti = pratiche.filter((p) => p.organization_id === oid).length;
        const pratDone = pratiche.filter(
          (p) => p.organization_id === oid && p.stato === "completata"
        ).length;
        const ords = ordiniCurr.filter((o) => ordineOrgId(o) === oid);
        const evasi = ords.filter(isEvaso).length;
        const cons = consCurr.filter((c) => c.organization_id === oid);
        const ok = cons.filter((c) => ["consegnata", "parziale"].includes(c.stato)).length;
        let extra = 0;
        let hasE = false;
        for (const o of ords.filter(isEvaso)) {
          const t = ordineTotale(o);
          if (t != null) {
            hasE = true;
            extra += t;
          }
        }
        return {
          id: oid,
          nome: s.denominazione || s.nome || oid,
          ospiti,
          pratiche: pratDone,
          ordiniEvasi: evasi,
          ordiniTotal: ords.length,
          consegneOk: ok,
          consegneTotal: cons.length,
          puntuali: cons.length ? `${Math.round((ok / cons.length) * 100)}%` : "—",
          extraLabel: hasE ? formatEuro(extra) : "—",
        };
      });

    return {
      praticheDone,
      praticheTrend: pctChange(praticheDone, pratichePrev.length),
      ordiniEvasi,
      ordiniTrend: pctChange(ordiniEvasi, ordiniEvasiPrev),
      puntualiPct,
      puntualiTrend:
        puntualiPct != null && puntualiPctPrev != null ? puntualiPct - puntualiPctPrev : null,
      assistiti,
      clientiN,
      ospitiN,
      valoreExtra: hasEuro ? valoreExtra : null,
      extraUnita,
      consOk,
      consFail,
      consegneTotal: consCurr.length,
      inCorso: consCurr.filter((c) =>
        ["in_consegna", "in_preparazione", "pianificata", "pronta"].includes(c.stato)
      ).length,
      weekBuckets,
      barOrdini,
      pieAssistiti,
      extraTop,
      tableRows,
      struttureCount: strutture.filter((s) => filteredOrg(s.organization_id)).length,
      updatedAt,
      alerts: {
        documenti: kpi.documenti_in_scadenza || 0,
        ricette: kpi.ricette_mancanti || 0,
        scorte: kpi.scorte_basse || 0,
        fatture: kpi.fatture_aperte || 0,
        terapie: kpi.terapie_da_visionare || 0,
      },
    };
  }, [
    ranges,
    pratiche,
    ordini,
    consegne,
    extraRows,
    strutture,
    kpi,
    ospitiCount,
    clientiCount,
    filteredOrg,
    updatedAt,
  ]);

  const exportCsv = () => {
    const lines = [
      ["Struttura", "Ospiti", "Pratiche completate", "Ordini evasi", "Consegne ok", "Extra"].join(";"),
    ];
    for (const r of stats.tableRows) {
      lines.push(
        [r.nome, r.ospiti, r.pratiche, r.ordiniEvasi, `${r.consegneOk}/${r.consegneTotal}`, r.extraLabel]
          .map((x) => `"${String(x).replace(/"/g, '""')}"`)
          .join(";")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-farmacia-${period.id}g-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const pieTotal = stats.pieAssistiti.reduce((a, b) => a + b.value, 0) || 1;

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <FarmaciaPageChrome>
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs"
            value={periodId}
            onChange={(e) => setPeriodId(e.target.value)}
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          >
            <option value="all">Tutte le strutture</option>
            {strutture.map((s) => (
              <option key={s.organization_id} value={s.organization_id}>
                {s.denominazione || s.nome}
              </option>
            ))}
          </select>
          <Button type="button" size="sm" className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta report
          </Button>
        </FarmaciaPageChrome>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          <KpiTile
            icon={ClipboardList}
            label="Pratiche completate"
            value={stats.praticheDone}
            trend={stats.praticheTrend}
            tone="emerald"
          />
          <KpiTile
            icon={ShoppingCart}
            label="Ordini evasi"
            value={stats.ordiniEvasi}
            trend={stats.ordiniTrend}
            tone="sky"
          />
          <KpiTile
            icon={Truck}
            label="Consegne completate"
            value={stats.puntualiPct != null ? `${stats.puntualiPct}%` : "—"}
            trend={stats.puntualiTrend}
            hint={
              stats.consegneTotal
                ? `${stats.consOk} su ${stats.consegneTotal} nel periodo`
                : "Nessuna consegna nel periodo"
            }
            tone="violet"
          />
          <KpiTile
            icon={Users}
            label="Assistiti attivi"
            value={stats.assistiti}
            hint={`${stats.ospitiN} in struttura · ${stats.clientiN} clienti`}
            tone="amber"
          />
          <KpiTile
            icon={Package}
            label="Valore extra"
            value={stats.valoreExtra != null ? formatEuro(stats.valoreExtra) : `${stats.extraUnita} u.`}
            hint={stats.valoreExtra != null ? undefined : "Unità consegnate (senza prezzo)"}
            tone="rose"
          />
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Andamento pratiche</h2>
              <span className="text-[10px] text-slate-400">Settimanale</span>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.weekBuckets}>
                  <defs>
                    <linearGradient id="praticheFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F5C4C" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#0F5C4C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Completate"
                    stroke="#0F5C4C"
                    fill="url(#praticheFill)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Ordini per struttura</h2>
              <span className="text-[10px] text-slate-400">{period.label}</span>
            </div>
            <div className="h-48">
              {stats.barOrdini.every((b) => b.count === 0) ? (
                <p className="flex h-full items-center justify-center text-xs text-slate-400">
                  Nessun ordine nel periodo
                </p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.barOrdini} layout="vertical" margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Ordini" fill="#0F5C4C" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Distribuzione assistiti</h3>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-28 w-28 shrink-0">
                {stats.pieAssistiti.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={stats.pieAssistiti} dataKey="value" innerRadius={28} outerRadius={44} paddingAngle={2}>
                        {stats.pieAssistiti.map((e) => (
                          <Cell key={e.name} fill={e.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-[10px] text-slate-400">N/D</div>
                )}
              </div>
              <ul className="min-w-0 flex-1 space-y-1.5 text-xs">
                {stats.pieAssistiti.map((e) => (
                  <li key={e.name} className="flex justify-between gap-2">
                    <span className="truncate text-slate-600">{e.name}</span>
                    <span className="font-medium text-slate-900">
                      {Math.round((e.value / pieTotal) * 100)}% · {e.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Performance consegne</h3>
            <ul className="mt-3 space-y-2 text-xs">
              <li className="flex justify-between gap-2">
                <span className="text-slate-600">Completate</span>
                <span className="font-medium text-emerald-700">{stats.consOk}</span>
              </li>
              <li className="flex justify-between gap-2">
                <span className="text-slate-600">In corso</span>
                <span className="font-medium text-sky-700">{stats.inCorso}</span>
              </li>
              <li className="flex justify-between gap-2">
                <span className="text-slate-600">Problemi / riprogrammate</span>
                <span className="font-medium text-rose-700">{stats.consFail}</span>
              </li>
              <li className="flex justify-between gap-2 border-t border-slate-100 pt-2">
                <span className="text-slate-600">Tasso completamento</span>
                <span className="font-semibold text-slate-900">
                  {stats.puntualiPct != null ? `${stats.puntualiPct}%` : "—"}
                </span>
              </li>
            </ul>
            <Button asChild variant="link" className="mt-2 h-auto px-0 text-xs text-emerald-700">
              <Link to="/farmacia/consegne">Vedi consegne</Link>
            </Button>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Extra per ospite</h3>
            <ul className="mt-3 space-y-2 text-xs">
              {stats.extraTop.length === 0 ? (
                <li className="text-slate-400">Nessun extra nel mese corrente</li>
              ) : (
                stats.extraTop.map(([name, qty]) => (
                  <li key={name} className="flex justify-between gap-2">
                    <span className="truncate text-slate-600">{name}</span>
                    <span className="font-medium text-slate-900">{qty} u.</span>
                  </li>
                ))
              )}
            </ul>
            <Button asChild variant="link" className="mt-2 h-auto px-0 text-xs text-emerald-700">
              <Link to="/farmacia/amministrazione?tab=extra">Vedi dettagli</Link>
            </Button>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Scadenze e alert</h3>
            <ul className="mt-3 space-y-2 text-xs">
              {[
                { label: "Documenti in scadenza", value: stats.alerts.documenti, to: "/farmacia/amministrazione?tab=scadenze", tone: "amber" },
                { label: "Ricette mancanti", value: stats.alerts.ricette, to: "/farmacia/pratiche?vista=copertura", tone: "rose" },
                { label: "Scorte basse", value: stats.alerts.scorte, to: "/farmacia/magazzino?tab=alert", tone: "amber" },
                { label: "Fatture aperte", value: stats.alerts.fatture, to: "/farmacia/amministrazione?tab=fatture", tone: "sky" },
                { label: "Terapie da visionare", value: stats.alerts.terapie, to: "/farmacia/pratiche", tone: "violet" },
              ].map((a) => (
                <li key={a.label}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left hover:underline"
                    onClick={() => navigate(a.to)}
                  >
                    <span className="text-slate-600">{a.label}</span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        a.value
                          ? "border-amber-200 bg-amber-50 text-amber-800"
                          : "border-slate-200 bg-slate-50 text-slate-500"
                      }`}
                    >
                      {a.value}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-800">Report strutture</h2>
            <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setPanelOpen(true)}>
              Dettaglio report
            </Button>
          </div>
          {stats.tableRows.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-slate-400">Nessuna struttura</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-left text-xs">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Struttura</th>
                    <th className="px-3 py-2.5 font-semibold">Ospiti</th>
                    <th className="px-3 py-2.5 font-semibold">Pratiche</th>
                    <th className="px-3 py-2.5 font-semibold">Ordini evasi</th>
                    <th className="px-3 py-2.5 font-semibold">Consegne</th>
                    <th className="px-3 py-2.5 font-semibold">Extra</th>
                    <th className="px-3 py-2.5 font-semibold">Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stats.tableRows.map((r) => {
                    const perf = performanceBadge(r);
                    const initials = (r.nome || "?")
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase();
                    return (
                      <tr
                        key={r.id}
                        className="cursor-pointer hover:bg-slate-50/80"
                        onClick={() => navigate(`/farmacia/struttura/${encodeURIComponent(r.id)}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0F5C4C] text-[10px] font-semibold text-white">
                              {initials}
                            </span>
                            <span className="font-medium text-slate-800">{r.nome}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{r.ospiti}</td>
                        <td className="px-3 py-3 text-slate-700">{r.pratiche}</td>
                        <td className="px-3 py-3 text-slate-700">
                          {r.ordiniEvasi}
                          <span className="text-slate-400">/{r.ordiniTotal}</span>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{r.puntuali}</td>
                        <td className="px-3 py-3 text-slate-700">{r.extraLabel}</td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className={`text-[10px] ${perf.className}`}>
                            {perf.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {panelOpen ? (
          <div className="xl:hidden">
            <ReportDetailPanel
              stats={stats}
              periodLabel={period.label}
              onExportCsv={exportCsv}
              onClose={() => setPanelOpen(false)}
            />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <ReportDetailPanel
          stats={stats}
          periodLabel={period.label}
          onExportCsv={exportCsv}
          onClose={() => setPanelOpen(false)}
        />
      </div>
    </div>
  );
}
